import { useQuery, UseQueryResult } from '@tanstack/react-query'

export interface PollingConfig<TData, TError> {
  // Essential props
  queryKey: unknown[]
  queryFn: () => Promise<TData>
  enabled?: boolean

  // Polling control props
  shouldPoll?: (data: TData | undefined, error: TError | null) => boolean
  pollInterval?: number

  // Optional props with defaults
  refetchIntervalInBackground?: boolean
  retryCount?: number
  retryDelay?: number | ((attemptIndex: number) => number)
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchOnReconnect?: boolean
}

export const usePollingQuery = <TData, TError = Error>(
  config: PollingConfig<TData, TError>
): UseQueryResult<TData, TError> => {
  const {
    queryKey,
    queryFn,
    enabled = true,
    shouldPoll,
    pollInterval = 2000,
    refetchIntervalInBackground = false,
    retryCount = 2,
    retryDelay = (attemptIndex: number) => Math.min(1000 * Math.pow(2, attemptIndex), 4000),
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    refetchOnReconnect = false
  } = config

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    refetchInterval: (query) => {
      if (!shouldPoll) return false

      const currentData = query?.state?.data
      const currentError = query?.state?.error

      return shouldPoll(currentData, currentError) ? pollInterval : false
    },
    refetchIntervalInBackground,
    retry: (failureCount) => failureCount < retryCount,
    retryDelay,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchOnReconnect
  })
}
